import { useEffect, useRef } from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise = null;

function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_URL;
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => { scriptPromise = null; reject(new Error('Turnstile failed to load')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile bot check. Renders nothing unless the build sets
 * VITE_TURNSTILE_SITE_KEY (the Worker then also needs TURNSTILE_SECRET_KEY).
 * Tokens are single use: give the component a new `key` after each attempt.
 */
export default function Turnstile({ onToken }) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const ref = useRef(null);

  useEffect(() => {
    if (!siteKey || !ref.current) return undefined;
    let widgetId = null;
    let cancelled = false;
    loadScript()
      .then((ts) => {
        if (cancelled || !ts || !ref.current) return;
        widgetId = ts.render(ref.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      })
      .catch(() => onToken(null));
    return () => {
      cancelled = true;
      if (widgetId !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId); } catch { /* ignore */ }
      }
    };
  }, [siteKey, onToken]);

  if (!siteKey) return null;
  return <div ref={ref} className="flex justify-center" />;
}

export function turnstileRequired() {
  return !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
}
